import { SearchService } from "./search.service";
import { Metadata } from "../../shared/models/metadata.model";
import { getResult, walkJson } from "./services.utils";
import { SourceItem } from "../../shared/models/source-item.model";
import { MetadataRequest } from "../../shared/models/metadata-request.model";

/***
 * This is the standard backend implementation that uses the API server to get data.
 */
export class MetadataServiceImpl implements SearchService {
    appAPIUrl: string;
    showSnackbar: (message: string, severity: string) => void;
    private readonly pendingRequests: Map<string, Promise<Array<SourceItem>>>;

    constructor(appAPIUrl: string, showSnackbar: (message: string, severity: string) => void) {
        this.appAPIUrl = appAPIUrl;
        this.showSnackbar = showSnackbar;
        this.pendingRequests = new Map<string, Promise<Array<SourceItem>>>();
    }

    public getPropertyValueSet = async (
        site: string,
        request: MetadataRequest,
        propertyName: string,
        isValidity: boolean,
        isSystemProperty: boolean,
    ): Promise<Array<SourceItem>> => {
        const dedupKey = this.getRequestDedupKey("valueset", {
            site,
            request,
            propertyName,
            isValidity,
            isSystemProperty
        });
        const existingRequest = this.pendingRequests.get(dedupKey);
        if (existingRequest) {
            return existingRequest;
        }

        const pendingRequest = (async (): Promise<Array<SourceItem>> => {
            const queryInfo = "?filters=" + encodeURIComponent(JSON.stringify(request)) + "&isValidity=" + isValidity + "&isSystemProperty=" + isSystemProperty;
            const res = await fetch(`${this.appAPIUrl}/search/valueset/${site}/${propertyName}${queryInfo}`, {
                method: "GET",
                credentials: 'include',
                cache: "no-cache",
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            const items: Array<SourceItem> = await getResult(res, true, this.showSnackbar);
            if (items) {
                return items.map((obj, ix) => {
                    return { ...obj, position: ix + 1 }
                });
            }
            return [];
        })();

        this.pendingRequests.set(dedupKey, pendingRequest);
        pendingRequest.finally(() => {
            this.pendingRequests.delete(dedupKey);
        });
        return pendingRequest;
    }

    public getPropertyNames = async (
        site: string,
        request: MetadataRequest,
    ): Promise<Array<SourceItem>> => {
        const dedupKey = this.getRequestDedupKey("propertynames", { site, request });
        const existingRequest = this.pendingRequests.get(dedupKey);
        if (existingRequest) {
            return existingRequest;
        }

        const pendingRequest = (async (): Promise<Array<SourceItem>> => {
            const queryInfo = "?filters=" + encodeURIComponent(JSON.stringify(request));
            const res = await fetch(`${this.appAPIUrl}/search/propertynames/${site}${queryInfo}`, {
                method: "GET",
                credentials: 'include',
                cache: "no-cache",
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            const items: Array<SourceItem> = await getResult(res, true, this.showSnackbar);
            if (items) {
                return items.map((obj, ix) => {
                    return { ...obj, position: ix + 1 }
                });
            }
            return [];
        })();

        this.pendingRequests.set(dedupKey, pendingRequest);
        pendingRequest.finally(() => {
            this.pendingRequests.delete(dedupKey);
        });
        return pendingRequest;
    }

    public search = async (
        site: string,
        request: MetadataRequest,
        unpublished?: boolean,
    ): Promise<Array<Metadata>> => {
        const queryInfo = "?filters=" + encodeURIComponent(JSON.stringify(request)) + (unpublished ? "&unpublished=true" : "");
        const res = await fetch(`${this.appAPIUrl}/search/metadata/${site}${queryInfo}`, {
            method: "GET",
            credentials: 'include',
            cache: "no-cache",
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (res.status === 200) {
            const resultMessage = await getResult(res, true, this.showSnackbar);
            if (resultMessage?.metadata) {
                this.fixDates(resultMessage);
                return resultMessage?.metadata;
            }
        }
        return [];
    }

    public searchSimilar = async (
        site: string,
        id: string,
        request: MetadataRequest,
    ): Promise<Array<Metadata>> => {
        const queryInfo = "?filters=" + encodeURIComponent(JSON.stringify(request));
        const res = await fetch(`${this.appAPIUrl}/search/similar/${site}/${id}${queryInfo}`, {
            method: "GET",
            credentials: 'include',
            cache: "no-cache",
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (res.status === 200) {
            const resultMessage = await getResult(res, true, this.showSnackbar);
            if (resultMessage?.metadata) {
                this.fixDates(resultMessage);
                return resultMessage?.metadata;
            }
        }
        return [];
    }

    private fixDates = (response: any): void => {
        walkJson(response?.metadata, (parent: { [key: string]: any }, key: string, value: any) => {
            if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) {
                // Express server sends dates in ISO string format. To get the real date values back we parse
                // any such string into Date
                parent[key] = new Date(Date.parse(value));
            }
        });
    }

    private getRequestDedupKey = (method: string, requestInput: unknown): string => {
        return `${method}:${this.stableSerialize(requestInput)}`;
    }

    private stableSerialize = (value: unknown): string => {
        if (value === null || value === undefined) {
            return String(value);
        }
        if (Array.isArray(value)) {
            return `[${value.map(item => this.stableSerialize(item)).join(",")}]`;
        }
        if (value instanceof Date) {
            return JSON.stringify(value.toISOString());
        }
        if (typeof value === "object") {
            const objectValue = value as Record<string, unknown>;
            const keys = Object.keys(objectValue).sort();
            return `{${keys.map(key => `${JSON.stringify(key)}:${this.stableSerialize(objectValue[key])}`).join(",")}}`;
        }
        return JSON.stringify(value);
    }

}
